import './currencies.css';
import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseSharpIcon from '@mui/icons-material/CloseSharp';

import Loading_animation_view from '../../loading_animation_view/loading_animation_view';

import {
    create_currency_rate,
    update_currency_rate,
    patch_currency_rate,
    get_currency_rates
} from '../../../services/company_admin_service';

const columns = [
    { id: 'code', label: "Code", minWidth: 0 },
    { id: 'name', label: "Devise", minWidth: 0 },
    { id: 'symbol', label: "Symbole", minWidth: 0 },
    { id: 'rate_to_default', label: "Rate → Default", minWidth: 0 },
    { id: 'is_default', label: "Default", minWidth: 0 },
    { id: 'is_active', label: "Actif", minWidth: 0 },
    { id: 'actions', label: "Actions", minWidth: 0 },
];

function ActiveBadge({ is_active }) {
    return (
        <>
            {is_active
                ? <Button variant="contained" className="status_success">Actif</Button>
                : <Button variant="contained" className="status_deny">Inactif</Button>}
        </>
    );
}

function DefaultBadge({ is_default }) {
    return (
        <>
            {is_default
                ? <Button variant="contained" className="status_success">Default</Button>
                : <span>-</span>}
        </>
    );
}

/**
 * Modal: Create / Edit currency rate
 * - For edit: rate editable only if not default
 * - is_active toggled here too
 */
function CurrencyModal({ open, mode, data, onClose, onSave }) {
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [symbol, setSymbol] = useState('');
    const [rateToDefault, setRateToDefault] = useState('');
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        if (!open) return;

        if (mode === "edit" && data) {
            setCode(data.code ?? '');
            setName(data.name ?? '');
            setSymbol(data.symbol ?? '');
            setRateToDefault(data.rate_to_default ?? '');
            setIsActive(!!data.is_active);
        } else {
            setCode('');
            setName('');
            setSymbol('');
            setRateToDefault('');
            setIsActive(true);
        }
    }, [open, mode, data]);

    const isDefault = !!data?.is_default;

    const canSave = () => {
        if (mode === "create") {
            return code.trim().length === 3 && name.trim() !== '' && String(rateToDefault).trim() !== '';
        }
        // edit
        if (isDefault) return false; // default is locked
        return String(rateToDefault).trim() !== '';
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {mode === "create" ? "Ajouter une devise" : "Modifier le rate"}
            </DialogTitle>

            <DialogContent>
                <div style={{ display: "flex", gap: "16px", marginTop: "8px" }}>
                    <TextField
                        variant='standard'
                        label="Code (MAD/EUR/USD)"
                        value={code}
                        onChange={(e) => setCode((e.target.value || '').toUpperCase())}
                        fullWidth
                        disabled={mode === "edit"}
                        inputProps={{ maxLength: 3 }}
                    />
                    <TextField
                        variant='standard'
                        label="Nom"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        fullWidth
                        disabled={mode === "edit"} // keep simple
                    />
                </div>

                <div style={{ display: "flex", gap: "16px", marginTop: "12px" }}>
                    <TextField
                        variant='standard'
                        label="Symbole"
                        value={symbol}
                        onChange={(e) => setSymbol(e.target.value)}
                        fullWidth
                        disabled={mode === "edit"} // keep simple
                    />

                    <TextField
                        variant='standard'
                        label="Rate → Default"
                        value={rateToDefault}
                        onChange={(e) => setRateToDefault(e.target.value)}
                        fullWidth
                        type="number"
                        disabled={mode === "edit" && isDefault}
                        helperText={mode === "edit" && isDefault ? "Devise par défaut : rate bloqué à 1" : "Ex: 1 EUR = 11.20 MAD"}
                    />
                </div>

                <div style={{ marginTop: "12px" }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                                disabled={mode === "edit" && isDefault}
                            />
                        }
                        label={(mode === "edit" && isDefault) ? "Actif (bloqué)" : (isActive ? "Actif" : "Inactif")}
                    />
                </div>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} className="submit_but cancel_but" variant="outlined">Annuler</Button>
                <Button
                    onClick={() => onSave({
                        code: code.trim().toUpperCase(),
                        name: name.trim(),
                        symbol: symbol.trim(),
                        rate_to_default: Number(rateToDefault),
                        is_active: isActive,
                    })}
                    variant="contained"
                    className="submit_but valide_but"
                    disabled={!canSave()}
                >
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function Currencies_list() {
    const history = useHistory();
    const [url_updated, setUrl_updated] = useState(null);

    const [rows, setRows] = useState(null);
    const [waiting, setWaiting] = useState(false);

    // modal
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("edit");
    const [selectedRow, setSelectedRow] = useState(null);

    useEffect(() => {
        setRows(null);

        async function load_data() {
            try {
                const res = await get_currency_rates();
                const arr = res.data || [];
                setRows(arr);
            } catch (e) {
               //  console.log(e?.response || e);
                setRows([]);
            }
        }

        load_data();
    }, [url_updated, history.location]);

    function openCreate() {
        setModalMode("create");
        setSelectedRow(null);
        setModalOpen(true);
    }

    function openEdit(row) {
        setModalMode("edit");
        setSelectedRow(row);
        setModalOpen(true);
    }

    async function onSave(payload) {
        try {
            setWaiting(true);

            if (modalMode === "create") {
                // If your backend supports creating currency + rate together
                await create_currency_rate(payload);
            } else {
                // Update rate + active status (rate table + maybe currency table)
                await update_currency_rate(selectedRow.code, {
                    rate_to_default: payload.rate_to_default,
                    is_active: payload.is_active,
                });
            }

            setModalOpen(false);
            setSelectedRow(null);
            setUrl_updated(Date.now());
            setWaiting(false);
        } catch (e) {
           //  console.log(e?.response || e);
            setWaiting(false);
        }
    }

    async function toggleActive(row, targetActive) {
        try {
            if (row.is_default) return;

            setWaiting(true);

            // If your PATCH expects { is_active: 0/1 }
            await patch_currency_rate(row.code, { is_active: targetActive ? 1 : 0 });

            // update local quickly
            const tmp = [...rows];
            const idx = tmp.findIndex(r => r.code === row.code);
            if (idx !== -1) tmp[idx].is_active = targetActive ? 1 : 0;
            setRows(tmp);

            setWaiting(false);
        } catch (e) {
           //  console.log(e?.response || e);
            setWaiting(false);
        }
    }

    const defaultCurrency = rows?.find(r => r.is_default == 1);

    return (
        <>
            {rows === null
                ? <Loading_animation_view />
                : <div className='services_list_container'>
                    <div className='services_list_container_header'>
                        <div className='title_container'>
                            <label>Gestion des devises </label>
                            <Button variant="contained" startIcon={<AddIcon />} className={waiting ? "submit_but disabled_button" : "submit_but"} onClick={openCreate}>
                                Ajouter
                            </Button>
                        </div>
                    </div>

                    <TableContainer className="services_list_table">
                        <Table aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell key={column.id} align="left">{column.label}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.length !== 0
                                    ? rows.map((row, id) => {
                                        const isDefault = row.is_default == 1;

                                        return (
                                            <TableRow key={"currency_" + id}>
                                                <TableCell align="left"><b>{row.code}</b></TableCell>
                                                <TableCell align="left">{row.name}</TableCell>
                                                <TableCell align="left">{row.symbol || '-'}</TableCell>
                                                <TableCell align="left">{row.rate_to_default}</TableCell>
                                                <TableCell align="left"><DefaultBadge is_default={isDefault} /></TableCell>

                                                <TableCell align="left">
                                                    <div className='documentsStatus_valide'>
                                                        <div>
                                                            <div>
                                                                <Tooltip title="Activer" placement="top">
                                                                    <span style={{ opacity: isDefault ? 0.4 : 1 }}>
                                                                        <CheckIcon
                                                                            className="checkIcon"
                                                                            onClick={() => { if (!isDefault && row.is_active != 1) toggleActive(row, true); }}
                                                                            style={{ cursor: (isDefault || row.is_active == 1) ? 'not-allowed' : 'pointer' }}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            </div>
                                                            <div>
                                                                <Tooltip title="Désactiver" placement="top">
                                                                    <span style={{ opacity: isDefault ? 0.4 : 1 }}>
                                                                        <CloseSharpIcon
                                                                            className="closeSharpIcon"
                                                                            onClick={() => { if (!isDefault && row.is_active == 1) toggleActive(row, false); }}
                                                                            style={{ cursor: (isDefault || row.is_active != 1) ? 'not-allowed' : 'pointer' }}
                                                                        />
                                                                    </span>
                                                                </Tooltip>
                                                            </div>
                                                        </div>
                                                        <ActiveBadge is_active={row.is_active == 1} />
                                                    </div>
                                                </TableCell>

                                                <TableCell align="left">
                                                    <div style={{ display: "flex", gap: "10px" }}>
                                                        <Tooltip title={isDefault ? "Devise par défaut (bloqué)" : "Modifier"} placement="top">
                                                            <span
                                                                className="editIcon"
                                                                onClick={() => { if (!isDefault) openEdit(row); }}
                                                                style={{ opacity: isDefault ? 0.4 : 1 }}
                                                            >
                                                                <EditIcon className="my_icon" style={{ cursor: isDefault ? 'not-allowed' : 'pointer' }} />
                                                            </span>
                                                        </Tooltip>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                    : <TableRow>
                                        <TableCell colSpan={7} align="center" style={{ padding: "20px" }}>
                                            Aucune devise disponible
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            }

            {modalOpen
                ? <CurrencyModal
                    open={modalOpen}
                    mode={modalMode}
                    data={selectedRow}
                    onClose={() => setModalOpen(false)}
                    onSave={onSave}
                />
                : <></>
            }

            <Backdrop className='waiting_backdrop' open={waiting}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );
}

export default Currencies_list;
